// Optional AI insights — uses the buyer's own Groq API key (from Settings).
// Asks Llama 3.3 70B to spot patterns the logic engine can't see
// (e.g. "you keep underquoting fintech projects by ~30%").

import { streamGroq, GroqMissingKeyError } from './groq-client'
import type { PortfolioStats, ProjectStats } from './engine'

const SYSTEM = `You are a no-nonsense pricing advisor for solo freelancers and consultants. The user gives you their actual time-tracking data; you find pricing patterns, underpricing risks, and concrete next-quote recommendations. Be specific with dollar amounts. Bullet your findings. Maximum 250 words.`

export async function aiPricingInsights(
  portfolio: PortfolioStats,
  projects: ProjectStats[],
  onStream: (chunk: string) => void
): Promise<void> {
  const payload = [
    `Portfolio: ${portfolio.projectCount} live projects, $${portfolio.totalQuoted.toFixed(0)} total quoted, ${portfolio.totalActualHours.toFixed(1)} actual hrs (vs ${portfolio.totalEstimatedHours.toFixed(1)} estimated). Blended rate $${portfolio.blendedHourlyRate.toFixed(0)}/hr. On track: ${portfolio.onTrackCount}. Over budget: ${portfolio.overBudgetCount}.`,
    '',
    'Per-project:',
    ...projects.map(
      (p) =>
        `- ${p.name} (${p.client}, ${p.status}): quoted $${p.quotedPrice}, est ${p.estimatedHours}h vs actual ${p.actualHours}h, effective $${p.effectiveHourlyRate.toFixed(0)}/hr, variance ${p.hoursVariancePct.toFixed(0)}%, health=${p.health}`
    ),
  ].join('\n')

  try {
    await streamGroq({
      messages: [
        { role: 'system', content: SYSTEM },
        {
          role: 'user',
          content: `Here's my time-tracking ledger. Tell me where I'm leaving money on the table and what I should charge next.\n\n${payload}`,
        },
      ],
      temperature: 0.4,
      onStream,
    })
  } catch (err) {
    if (err instanceof GroqMissingKeyError) {
      onStream(
        '\n\n⚠️ Click the Settings gear ⚙ in the header to add your free Groq API key. The core math above works fully without AI.'
      )
      return
    }
    onStream(`\n\n⚠️ ${err instanceof Error ? err.message : 'Groq error'}`)
  }
}
